import { ThinkingContent, AnthropicThinkingContent } from "@/services/promptEditor.service";
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/outline";
import React, { useState } from "react";
import { AutoFormattedText, LanguageSelectLocation } from "@components/library/FormattedText";

interface ThinkingRendererProps {
  thinking: ThinkingContent[] | null;
  languageSelectLocation?: LanguageSelectLocation;
  className?: string;
}

/**
 * Renders the thinking content of a ChatMessage. The component assumes the Anthropic format for thinking:
 * ThinkingContent[] will contain at most two blocks: a non-redacted one, a redacted one, or both.
 * @param thinking - The thinking content to render.
 * @param languageSelectLocation - Options for rendering the text. e.g. Markdown. Used on LogDrawer
 * @param className - Styling applied to the parent container.
 * @returns The rendered component.
 */
const ThinkingRenderer: React.FC<ThinkingRendererProps> = ({ thinking, languageSelectLocation, className = "" }) => {
  const [isThinkingOpen, setIsThinkingOpen] = useState(false);

  // Return null if thinking is null or empty
  if (!thinking || thinking.length === 0) return null;

  // Find redacted and non-redacted thinking content
  // Anthropic has at most two blocks when thinking is used: a non-redacted one, and an optional redacted one
  const redactedThinking = thinking.find((item) => item.type === "redacted_thinking");
  const nonRedactedThinking = thinking.find((item) => item.type === "thinking");

  // If neither type exists, return null
  if (!redactedThinking && !nonRedactedThinking) return null;

  return (
    <div className={`flex flex-col ${className}`}>
      <button
        className="flex cursor-pointer items-center gap-6 text-12-12 font-medium text-icon-base-2"
        onClick={() => setIsThinkingOpen(!isThinkingOpen)}
      >
        {isThinkingOpen ? <ChevronUpIcon className="h-16 w-16" /> : <ChevronDownIcon className="h-16 w-16" />}
        Reasoning
      </button>

      {isThinkingOpen && (
        <div className="mt-8 text-text-base-2">
          {/* Always display redacted thinking first, then non-redacted thinking */}
          <>
            {redactedThinking && (
              <div className="mb-4 px-8 pt-8 text-14-24 text-text-base-2">
                <div className="font-medium">The provider has redacted some thinking content for safety reasons.</div>
              </div>
            )}

            {nonRedactedThinking && (
              <div className="mb-4 px-8 pt-8 text-14-24 text-text-base-3">
                {languageSelectLocation ? (
                  <AutoFormattedText
                    text={(nonRedactedThinking as AnthropicThinkingContent).thinking}
                    languageSelectLocation={languageSelectLocation}
                  />
                ) : (
                  <div>{(nonRedactedThinking as AnthropicThinkingContent).thinking}</div>
                )}
              </div>
            )}
          </>
        </div>
      )}
    </div>
  );
};

export default ThinkingRenderer;
